/**
 * Replay → render-frame adapter (PRD §7.2 + §7.3).
 *
 * Given a `ParsedBattle` from `@pilot-protocol/replay-parser`, expose
 * the per-tick render input the scene needs. For P0/P1 a "tick" is a
 * turn — interpolation between turns lands in a later phase.
 *
 * Pure transform; no React, no DOM, no physics. The scene reads what
 * this returns and renders.
 */

import type { BattleState, MechState, ParsedBattle } from "@pilot-protocol/replay-parser";

export interface RenderFrame {
  /** Engine turn index — 0..final_turn. */
  tick: number;
  /** Engine state at this tick. Reference; do not mutate. */
  state: BattleState;
  /** Convenience pointers; identical to `state.mech_a` / `state.mech_b`. */
  mechA: MechState;
  mechB: MechState;
}

export function frameAt(battle: ParsedBattle, tick: number): RenderFrame {
  const clamped = clampTick(battle, tick);
  const state = battle.states[clamped];
  return {
    tick: clamped,
    state,
    mechA: state.mech_a,
    mechB: state.mech_b,
  };
}

export function lastTick(battle: ParsedBattle): number {
  return Math.max(0, battle.states.length - 1);
}

export function clampTick(battle: ParsedBattle, tick: number): number {
  const last = lastTick(battle);
  if (Number.isNaN(tick)) return 0;
  if (tick === Number.NEGATIVE_INFINITY || tick < 0) return 0;
  if (tick === Number.POSITIVE_INFINITY || tick > last) return last;
  return Math.floor(tick);
}
